/**
 * Pagination côté client, partagée par les tables des deux étapes.
 *
 * Les deux endpoints renvoient tout d'un coup (`size=9999`) : il n'y a donc rien à
 * demander au serveur, seulement une tranche à découper à l'affichage.
 */

/** Tailles de page proposées, la plus large par défaut comme sur la maquette. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250];

export const DEFAULT_PAGE_SIZE = 100;

/** Bornes affichées : « résultats 1 – 7 sur 7 ». Toutes deux à 0 sur une liste vide. */
export type PageRange = { from: number; to: number };

/** Au moins une page, même sans résultat : « 1 / 1 » reste juste et lisible. */
export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * Ramène un numéro de page dans les bornes.
 *
 * Indispensable après une action qui raccourcit la liste — un envoi, par exemple :
 * la page courante peut se retrouver au-delà de la dernière. On dérive au rendu plutôt
 * que de synchroniser un état par effet.
 */
export function clampPage(page: number, total: number, pageSize: number): number {
  return Math.min(Math.max(1, page), pageCount(total, pageSize));
}

export function pageRows<T>(rows: T[], page: number, pageSize: number): T[] {
  const start = (clampPage(page, rows.length, pageSize) - 1) * pageSize;

  return rows.slice(start, start + pageSize);
}

export function pageRange(total: number, page: number, pageSize: number): PageRange {
  if (total === 0) {
    return { from: 0, to: 0 };
  }

  const start = (clampPage(page, total, pageSize) - 1) * pageSize;

  return { from: start + 1, to: Math.min(start + pageSize, total) };
}
